/**
 * AdminFormModal — the shell of a corp-style Admin add / edit modal:
 *   - the heading (blue "Edit …" on an existing record) and its description;
 *   - the body (the page's fields);
 *   - corp's footer: the "Required before …" / "Suggestions before …" strips,
 *     then, on an edit with changes, "Unsaved changes" + Revert, and Cancel and
 *     the save button (off while anything required is missing);
 *   - closing with changes asks first ("Discard changes?").
 * There is no Delete in it: deleting is a row action. Escape does not close it,
 * so a long form is not lost to a stray key.
 *
 * On a Locked Admin page (useAdminPageLevel, plan 2026-10-10-001 KTD5) it is a
 * viewer, and says nothing about being locked (R18):
 *   - the body and `pinned` sit in a locked FieldLockScope
 *     (components/ui/FieldLock.tsx), so the shared inputs show their values,
 *     refuse changes and are not greyed out;
 *   - the title drops a leading "Edit " ("Edit Product" → "Product"), in the
 *     plain heading colour;
 *   - the footer is a single Close (after any `footerExtra`): no Save,
 *     Cancel, Revert, "Unsaved changes" or required / suggestion strips, and
 *     Close never asks to discard.
 * A Locked exception (KTD6, e.g. a product's facilities): pass `lockedSave`
 * and wrap the fields that may still change in <AdminLockException>
 * (AdminLockedFieldset.tsx); everything else stays read-only. The footer then
 * keeps Revert, Cancel and Save, and Save is on only while `dirty && canSave`.
 * `errors` and `suggestions` describe fields the user cannot change there, so
 * they are neither shown nor enforced; the page saves only its exception fields.
 */
import { useState, type ReactNode } from 'react';
import { AlertTriangle, RotateCcw } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { FieldLockScope } from '@/components/ui/FieldLock';
import { useAdminPageLevel } from './AdminPageLevelContext';
import { DialogFooterLayout, DialogHeading } from './DialogChrome';

type ModalSize = React.ComponentProps<typeof Modal>['size'];

export interface AdminFormModalProps {
  title: string;
  description?: ReactNode;
  isNew: boolean;
  dirty: boolean;
  saving: boolean;
  /** Listed above the footer; the save button is off while there are any. */
  errors?: string[];
  suggestions?: string[];
  /** Defaults to Create / Update, as corp. */
  saveLabel?: string;
  /** False keeps the save button off for another reason (e.g. nothing changed). */
  canSave?: boolean;
  cancelLabel?: string;
  /** Shown in the footer row before Cancel (e.g. corp's "Total:" price). */
  footerExtra?: ReactNode;
  onSave: () => void;
  onRevertAll: () => void;
  /** Close for good (after the discard prompt, when there are changes). */
  onClose: () => void;
  size?: ModalSize;
  /** The panel's width, after the size (e.g. corp's 1200px dialog). */
  panelClassName?: string;
  /** Held above the scrolling fields (the Modal's `pinned`). */
  pinned?: ReactNode;
  /**
   * The page has a Locked exception (KTD6): on a Locked page keep Save for the
   * fields wrapped in <AdminLockException>. Ignored on an Editable page.
   */
  lockedSave?: boolean;
  children: ReactNode;
}

export function AdminFormModal({
  title,
  description,
  isNew,
  dirty,
  saving,
  errors = [],
  suggestions = [],
  saveLabel,
  canSave = true,
  cancelLabel = 'Cancel',
  footerExtra,
  onSave,
  onRevertAll,
  onClose,
  size = 'lg',
  panelClassName,
  pinned,
  lockedSave = false,
  children,
}: AdminFormModalProps) {
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const locked = useAdminPageLevel() === 'locked';
  // Locked with no exception: nothing can change, so there is nothing to save or discard.
  const viewer = locked && !lockedSave;

  function requestClose() {
    if (saving) return;
    if (dirty && !viewer) setConfirmDiscard(true);
    else onClose();
  }

  const footer = viewer ? (
    <DialogFooterLayout>
      <div className="flex-1" />
      {footerExtra}
      <Button variant="secondary" onClick={onClose}>
        Close
      </Button>
    </DialogFooterLayout>
  ) : (
    <DialogFooterLayout errors={locked ? [] : errors} suggestions={locked ? [] : suggestions} isCreating={isNew}>
      {!isNew && dirty && (
        <div className="flex items-center gap-2">
          <span className="flex items-center gap-1.5 text-sm text-blue-700">
            <AlertTriangle className="h-4 w-4" aria-hidden />
            Unsaved changes
          </span>
          <Button
            variant="ghost"
            size="xs"
            className="h-7 px-2 text-xs text-slate-500 hover:text-slate-700"
            leftIcon={<RotateCcw className="h-3.5 w-3.5" />}
            aria-label="Revert all changes"
            onClick={onRevertAll}
          >
            Revert
          </Button>
        </div>
      )}
      <div className="flex-1" />
      {footerExtra}
      <Button variant="secondary" onClick={requestClose} disabled={saving}>
        {cancelLabel}
      </Button>
      <Button onClick={onSave} loading={saving} disabled={locked ? !dirty || !canSave : errors.length > 0 || !canSave}>
        {saveLabel ?? (isNew ? 'Create' : 'Update')}
      </Button>
    </DialogFooterLayout>
  );

  const heading = locked ? title.replace(/^Edit\s+/, '') : title;

  return (
    <Modal
      isOpen
      onClose={requestClose}
      closeOnEscape={false}
      size={size}
      panelClassName={panelClassName}
      pinned={pinned != null ? <FieldLockScope locked={locked}>{pinned}</FieldLockScope> : pinned}
      title={<DialogHeading title={heading} variant={isNew || locked ? 'default' : 'edit'} description={description} />}
      footer={footer}
    >
      {/* Always the same Provider, so a level change does not remount the body. */}
      <FieldLockScope locked={locked}>{children}</FieldLockScope>
      <ConfirmDialog
        isOpen={confirmDiscard}
        onClose={() => setConfirmDiscard(false)}
        onConfirm={onClose}
        title="Discard changes?"
        message="You have unsaved changes. Close without saving them?"
        confirmLabel="Discard changes"
        variant="warning"
      />
    </Modal>
  );
}
